import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  Link2,
  Zap,
  Copy,
  Check,
  Trash2,
  FileText,
  Clock,
  ChevronRight,
  Download,
  FileDown,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Layers,
} from 'lucide-react';
import { API_BASE } from '../config';
import { useConfirm } from '../contexts/ConfirmContext';
import {
  downloadResumePdf,
  downloadCoverLetterPdf,
  downloadResumeDocx,
  downloadCoverLetterDocx,
  downloadCvPdf,
  downloadConsolidatedPdf,
  downloadCvDocx,
  type ResumeData,
  type CoverLetterData,
  type CvData,
} from '../utils/documentTemplates';

// ── Types ─────────────────────────────────────────────────────────────────────

interface GeneratedApplication {
  id: number;
  job_title: string;
  company_name: string;
  job_description: string;
  ats_score_estimate: number;
  resume: ResumeData;
  cover_letter: CoverLetterData;
  cv: CvData | null;
  generated_at: string;
  job_url?: string;
}

interface HistoryItem {
  id: number;
  job_url: string;
  job_title: string;
  company_name: string;
  ats_score_estimate: number;
  created_at: string;
}

interface EvaluationSummary {
  id: string;
  recommendation: 'apply' | 'skip' | 'review';
  hard_filter_status: 'pass' | 'fail' | 'unknown';
  overall_score: number;
}

const LOADING_STEPS = [
  'Evaluating role fit...',
  'Fetching job posting...',
  'Extracting requirements...',
  'Generating ATS resume...',
  'Compiling comprehensive CV...',
  'Writing cover letter...',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAge(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function atsColor(score: number): string {
  if (score >= 80) return 'text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-900/30 dark:border-green-800';
  if (score >= 60) return 'text-primary-700 bg-primary-50 border-primary-200 dark:text-primary-400 dark:bg-primary-900/30 dark:border-primary-800';
  if (score >= 40) return 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-900/30 dark:border-amber-800';
  return 'text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-800';
}

function formatResumeAsText(resume: ResumeData | null): string {
  if (!resume) return '';
  const lines: string[] = [];
  const hr = '─'.repeat(60);

  lines.push(resume.contact.name.toUpperCase());
  lines.push(resume.headline);
  lines.push(hr);
  const contact = [resume.contact.email, resume.contact.phone, resume.contact.location].filter(Boolean).join('  |  ');
  if (contact) lines.push(contact);
  lines.push('');

  lines.push('PROFESSIONAL SUMMARY');
  lines.push(resume.professional_summary ?? '');
  lines.push('');

  if (resume.core_competencies?.length > 0) {
    lines.push('CORE COMPETENCIES');
    const cols = 3;
    const skills = resume.core_competencies.slice(0, 12);
    for (let i = 0; i < skills.length; i += cols) {
      lines.push(
        skills.slice(i, i + cols).map((s) => `• ${s.padEnd(22)}`).join(''),
      );
    }
    lines.push('');
  }

  if (resume.experience?.length > 0) {
    lines.push('PROFESSIONAL EXPERIENCE');
    for (const exp of resume.experience) {
      lines.push(`${exp.role.toUpperCase()}  |  ${exp.company}  |  ${exp.dates}`);
      for (const b of exp.bullets ?? []) lines.push(`  • ${b}`);
      lines.push('');
    }
  }

  if (resume.education?.length > 0) {
    lines.push('EDUCATION');
    for (const edu of resume.education) {
      lines.push(`${edu.degree}  |  ${edu.school}  |  ${edu.year}`);
    }
    lines.push('');
  }

  if ((resume.certifications?.length ?? 0) > 0) {
    lines.push('CERTIFICATIONS');
    for (const cert of resume.certifications ?? []) lines.push(`• ${cert}`);
    lines.push('');
  }

  const tech = resume.technical_skills;
  if (tech && Object.keys(tech).length > 0) {
    lines.push('TECHNICAL SKILLS');
    for (const [cat, skills] of Object.entries(tech)) {
      lines.push(`${cat}: ${(skills as string[]).join(', ')}`);
    }
  }

  return lines.join('\n');
}

function formatCoverLetterAsText(cl: CoverLetterData | null, contactName: string): string {
  if (!cl) return '';
  return [
    cl.date,
    '',
    cl.recipient,
    cl.company,
    '',
    `RE: ${cl.subject}`,
    '',
    `Dear ${cl.recipient},`,
    '',
    cl.opening,
    '',
    cl.value_prop,
    '',
    cl.closing,
    '',
    `${cl.sign_off},`,
    '',
    contactName,
  ].join('\n');
}

function formatCvAsText(cv: CvData | null): string {
  if (!cv) return '';
  const lines: string[] = [];
  const hr = '─'.repeat(60);
  lines.push(cv.contact.name.toUpperCase());
  lines.push(hr);
  const contact = [cv.contact.email, cv.contact.phone, cv.contact.location].filter(Boolean).join('  |  ');
  if (contact) lines.push(contact);
  lines.push('');
  if (cv.profile_summary) {
    lines.push('PROFESSIONAL PROFILE');
    lines.push(cv.profile_summary);
    lines.push('');
  }
  if (cv.key_achievements?.length > 0) {
    lines.push('KEY ACHIEVEMENTS');
    for (const a of cv.key_achievements) lines.push(`▪ ${a}`);
    lines.push('');
  }
  if (cv.experience?.length > 0) {
    lines.push('CAREER HISTORY');
    for (const exp of cv.experience) {
      lines.push(`${exp.role}  |  ${exp.company}  |  ${exp.dates}`);
      if (exp.description) lines.push(`  ${exp.description}`);
      for (const b of exp.bullets ?? []) lines.push(`  • ${b}`);
      lines.push('');
    }
  }
  if (cv.skills && Object.keys(cv.skills).length > 0) {
    lines.push('SKILLS');
    for (const [cat, skills] of Object.entries(cv.skills)) {
      lines.push(`${cat}: ${(skills as string[]).join(', ')}`);
    }
    lines.push('');
  }
  if (cv.education?.length > 0) {
    lines.push('EDUCATION');
    for (const edu of cv.education) {
      lines.push(`${edu.degree}${edu.field ? `, ${edu.field}` : ''}  |  ${edu.school}  |  ${edu.year}${edu.honors ? `  |  ${edu.honors}` : ''}`);
    }
    lines.push('');
  }
  if (cv.certifications?.length > 0) {
    lines.push('CERTIFICATIONS');
    for (const cert of cv.certifications) lines.push(`• ${cert}`);
  }
  return lines.join('\n');
}

// ── CopyButton ─────────────────────────────────────────────────────────────

function CopyButton({ text, label = 'Copy All' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // silently fail
    }
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-navy-800 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition-colors"
    >
      {copied ? <Check size={13} className="text-green-600" /> : <Copy size={13} />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const JobTargeting: React.FC = () => {
  const [step, setStep] = useState<'input' | 'loading' | 'result'>('input');
  const { prompt } = useConfirm();
  const [url, setUrl] = useState('');
  const [inputMode, setInputMode] = useState<'url' | 'text'>('url');
  const [jdText, setJdText] = useState('');
  const [result, setResult] = useState<GeneratedApplication | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [evaluation, setEvaluation] = useState<EvaluationSummary | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/apply-optimizer/history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json() as { items: HistoryItem[] };
        setHistory(data.items ?? []);
      }
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const handleGenerate = async () => {
    const usingText = inputMode === 'text';
    if (usingText ? !jdText.trim() : !url.trim()) return;
    setError(null);
    setStep('loading');
    setLoadingStep(0);

    // Animate progress steps
    const timers = [
      setTimeout(() => setLoadingStep(1), 800),
      setTimeout(() => setLoadingStep(2), 2200),
      setTimeout(() => setLoadingStep(3), 4500),
    ];

    try {
      let generateBody: Record<string, unknown>;

      if (usingText) {
        // Paste-JD mode: skip URL evaluation and generate directly from the pasted text.
        generateBody = { jd_text: jdText.trim(), target_format: 'resume_cv_cover_letter' };
      } else {
        // URL mode: evaluate the role first, then generate via the evaluation report.
        const evaluationRes = await fetch(`${API_BASE}/api/evaluations`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ job_url: url.trim() }),
        });
        if (!evaluationRes.ok) {
          const err = await evaluationRes.json() as { error?: string };
          throw new Error(err.error || 'Could not evaluate this role');
        }
        const evaluationData = await evaluationRes.json() as { evaluation_report: EvaluationSummary };
        const nextEvaluation = evaluationData.evaluation_report;
        setEvaluation(nextEvaluation);

        let override: { reason: string } | undefined;
        if (nextEvaluation.recommendation !== 'apply') {
          const reason = await prompt({
            title: 'Generate anyway?',
            message: `This role is marked "${nextEvaluation.recommendation}". Enter a reason to generate anyway, or cancel.`,
            inputLabel: 'Reason',
            placeholder: 'Why generate despite the recommendation?',
            confirmLabel: 'Generate',
            required: true,
          });
          if (!reason?.trim()) {
            setError(`Generation cancelled because the role is marked ${nextEvaluation.recommendation}.`);
            setStep('input');
            timers.forEach(clearTimeout);
            return;
          }
          override = { reason: reason.trim() };
        }
        generateBody = { evaluation_report_id: nextEvaluation.id, target_format: 'resume_cv_cover_letter', override };
      }

      const res = await fetch(`${API_BASE}/api/apply-optimizer/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(generateBody),
      });

      timers.forEach(clearTimeout);

      if (!res.ok) {
        const err = await res.json() as { error?: string | { message?: string }; qa_report?: { reasons?: string[] } };
        let msg = typeof err.error === 'string'
          ? err.error
          : (err.error as { message?: string })?.message ?? `HTTP ${res.status}`;
        // Surface QA hard-block reasons clearly (e.g. unresolved company/role).
        if (err.qa_report?.reasons?.length) {
          msg = `${msg} (${err.qa_report.reasons.join(', ')})`;
        }
        throw new Error(msg);
      }

      const data = await res.json() as GeneratedApplication;
      data.job_url = data.job_url || (usingText ? '' : url.trim());
      setResult(data);
      setStep('result');
      void loadHistory();
    } catch (err: unknown) {
      timers.forEach(clearTimeout);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setStep('input');
    }
  };

  const handleLoadHistory = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/apply-optimizer/${id}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json() as GeneratedApplication;
      setResult(data);
      setStep('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      // silently fail
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await fetch(`${API_BASE}/api/apply-optimizer/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setHistory((prev) => prev.filter((h) => h.id !== id));
      if (result?.id === id) {
        setResult(null);
        setStep('input');
      }
    } catch {
      // silently fail
    }
  };

  const handleDownload = async (type: 'resume-pdf' | 'resume-docx' | 'cl-pdf' | 'cl-docx' | 'cv-pdf' | 'cv-docx' | 'combined') => {
    if (!result) return;
    setDownloading(type);
    try {
      const name = result.resume?.contact?.name ?? 'Candidate';
      const email = result.resume?.contact?.email ?? '';
      const phone = result.resume?.contact?.phone ?? '';
      const company = result.company_name ?? '';
      const role = result.job_title ?? '';
      if (type === 'resume-pdf') {
        downloadResumePdf(result.resume, result.job_title, result.company_name);
      } else if (type === 'resume-docx') {
        await downloadResumeDocx(result.resume, result.job_title);
      } else if (type === 'cl-pdf') {
        downloadCoverLetterPdf(result.cover_letter, name, email, phone);
      } else if (type === 'cl-docx') {
        await downloadCoverLetterDocx(result.cover_letter, name, email, phone);
      } else if (type === 'cv-pdf') {
        if (result.cv) downloadCvPdf(result.cv, name, company, role);
      } else if (type === 'cv-docx') {
        if (result.cv) await downloadCvDocx(result.cv);
      } else if (type === 'combined') {
        if (result.cv) downloadConsolidatedPdf(result.cv, result.cover_letter, name, email, phone, company, role);
      }
    } catch {
      // silently fail
    } finally {
      setDownloading(null);
    }
  };

  const resumeText = formatResumeAsText(result?.resume ?? null);
  const coverText = formatCoverLetterAsText(result?.cover_letter ?? null, result?.resume?.contact?.name ?? '');
  const cvText = formatCvAsText(result?.cv ?? null);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Target size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Job Targeting</h1>
            <p className="text-sm text-gray-500 dark:text-slate-400">Evaluate a role, then generate an ATS resume, CV, and cover letter</p>
          </div>
        </div>
        {step === 'result' && (
          <button
            onClick={() => { setStep('input'); setUrl(''); }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-navy-800 hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-400 transition-colors"
          >
            <RefreshCw size={14} />
            New Application
          </button>
        )}
      </div>

      {/* Error Banner */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
          >
            <AlertCircle size={18} className="text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-400">Could not generate application</p>
              <p className="text-sm text-red-600 dark:text-red-500 mt-0.5">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600 dark:hover:text-red-300">✕</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Step 1: Input ─────────────────────────────────────────────────── */}
      {step === 'input' && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Evaluate a role &amp; generate documents</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
              Provide a job URL (LinkedIn, Greenhouse, Lever, Ashby, Indeed, company sites), or paste the job description text directly.
            </p>

            {/* Input mode toggle */}
            <div className="inline-flex mb-3 rounded-lg border border-gray-200 dark:border-slate-700 p-0.5 bg-gray-50 dark:bg-slate-800/50">
              {(['url', 'text'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setInputMode(m)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    inputMode === m
                      ? 'bg-indigo-600 text-white'
                      : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                  }`}
                >
                  {m === 'url' ? 'Job URL' : 'Paste JD text'}
                </button>
              ))}
            </div>

            {inputMode === 'url' ? (
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Link2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500" />
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleGenerate(); }}
                    placeholder="https://boards.greenhouse.io/company/jobs/123456"
                    className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-offset-navy-900 focus:border-transparent bg-gray-50 dark:bg-slate-800/50 dark:text-white dark:placeholder-slate-500"
                  />
                </div>
                <button
                  onClick={() => void handleGenerate()}
                  disabled={!url.trim()}
                  className="flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  <Zap size={15} />
                  Evaluate &amp; Generate
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <textarea
                  value={jdText}
                  onChange={(e) => setJdText(e.target.value)}
                  rows={8}
                  placeholder="Paste the full job description here (role title, company, responsibilities, requirements)…"
                  className="w-full px-4 py-3 text-sm border border-gray-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gray-50 dark:bg-slate-800/50 dark:text-white dark:placeholder-slate-500"
                />
                <button
                  onClick={() => void handleGenerate()}
                  disabled={!jdText.trim()}
                  className="flex items-center gap-2 px-5 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  <Zap size={15} />
                  Generate from JD
                </button>
              </div>
            )}
            {evaluation && (
              <div className="mt-4 rounded-lg border border-gray-200 dark:border-slate-700 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">Last evaluation</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    evaluation.recommendation === 'apply'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      : evaluation.recommendation === 'skip'
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                  }`}>
                    {evaluation.recommendation}
                  </span>
                  <span className="text-gray-500 dark:text-slate-400">Score {evaluation.overall_score}</span>
                  <span className="text-gray-500 dark:text-slate-400">Hard filters: {evaluation.hard_filter_status}</span>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-400 dark:text-slate-500">
              <div className="flex gap-2 flex-wrap">
                {['LinkedIn Jobs', 'Greenhouse', 'Lever', 'Ashby', 'Indeed', 'Company Sites'].map((s) => (
                  <span key={s} className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 rounded-full text-gray-500 dark:text-slate-400">{s}</span>
                ))}
              </div>
            </div>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Recent Applications</h2>
              <div className="space-y-2">
                {history.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => void handleLoadHistory(item.id)}
                    className="flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-700 hover:bg-indigo-50/30 dark:hover:bg-indigo-900/20 cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                        <FileText size={14} className="text-indigo-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.job_title}</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{item.company_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${atsColor(item.ats_score_estimate)}`}>
                        ATS {item.ats_score_estimate}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-slate-500 flex items-center gap-1">
                        <Clock size={11} />
                        {formatAge(item.created_at)}
                      </span>
                      <ChevronRight size={14} className="text-gray-400 dark:text-slate-500 group-hover:text-indigo-500 transition-colors" />
                      <button
                        onClick={(e) => void handleDelete(item.id, e)}
                        className="p-1 text-gray-300 dark:text-slate-600 hover:text-red-500 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* ── Step 2: Loading ───────────────────────────────────────────────── */}
      {step === 'loading' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm p-10"
        >
          <div className="max-w-sm mx-auto">
            {/* Animated icon */}
            <div className="flex justify-center mb-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center"
              >
                <Zap size={28} className="text-white" />
              </motion.div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">Crafting Your Application</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 text-center mb-8">AI is analyzing the job and tailoring your documents…</p>

            <div className="space-y-4">
              {LOADING_STEPS.map((stepLabel, i) => {
                const isDone = i < loadingStep;
                const isActive = i === loadingStep;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: isDone || isActive ? 1 : 0.3, x: 0 }}
                    transition={{ delay: i * 0.15 }}
                    className="flex items-center gap-3"
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                      isDone ? 'bg-green-100 dark:bg-green-900/40' : isActive ? 'bg-indigo-100 dark:bg-indigo-900/40' : 'bg-gray-100 dark:bg-slate-700'
                    }`}>
                      {isDone ? (
                        <Check size={14} className="text-green-600" />
                      ) : isActive ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        >
                          <RefreshCw size={13} className="text-indigo-600" />
                        </motion.div>
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-slate-500" />
                      )}
                    </div>
                    <span className={`text-sm ${isDone ? 'text-green-700 dark:text-green-400 line-through' : isActive ? 'text-indigo-700 dark:text-indigo-400 font-medium' : 'text-gray-400 dark:text-slate-500'}`}>
                      {stepLabel}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Step 3: Results ───────────────────────────────────────────────── */}
      {step === 'result' && result && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Job header card */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold">{result.job_title}</h2>
                <p className="text-indigo-200 text-sm mt-1">{result.company_name}</p>
                <p className="text-indigo-300 text-xs mt-2 flex items-center gap-1">
                  <Clock size={11} />
                  Generated {formatAge(result.generated_at)}
                </p>
              </div>
              <div className="text-right">
                <div className={`inline-flex flex-col items-center px-4 py-2 rounded-xl border-2 ${
                  result.ats_score_estimate >= 80 ? 'bg-green-500/20 border-green-400' :
                  result.ats_score_estimate >= 60 ? 'bg-primary-500/20 border-primary-400' :
                  result.ats_score_estimate >= 40 ? 'bg-amber-500/20 border-amber-400' :
                  'bg-red-500/20 border-red-400'
                }`}>
                  <span className="text-3xl font-black text-white">{result.ats_score_estimate}</span>
                  <span className="text-xs text-indigo-200">ATS Score</span>
                </div>
              </div>
            </div>
          </div>

          {/* Download panel */}
          <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <Download size={16} className="text-gray-700 dark:text-slate-300" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Download Documents</h3>
            </div>
            <div className="space-y-2.5">
              {/* Resume row */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                    <FileText size={14} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Resume</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">ATS-optimised for this role</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => void handleDownload('resume-pdf')}
                    disabled={downloading === 'resume-pdf'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg text-red-700 dark:text-red-400 transition-colors disabled:opacity-60"
                  >
                    <FileDown size={13} />{downloading === 'resume-pdf' ? '…' : 'PDF'}
                  </button>
                  <button
                    onClick={() => void handleDownload('resume-docx')}
                    disabled={downloading === 'resume-docx'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg text-primary-700 dark:text-primary-400 transition-colors disabled:opacity-60"
                  >
                    <FileText size={13} />{downloading === 'resume-docx' ? '…' : 'Word'}
                  </button>
                </div>
              </div>
              {/* CV row */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
                    <FileText size={14} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Curriculum Vitae</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Complete career record</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => void handleDownload('cv-pdf')}
                    disabled={!result.cv || downloading === 'cv-pdf'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg text-red-700 dark:text-red-400 transition-colors disabled:opacity-60"
                  >
                    <FileDown size={13} />{downloading === 'cv-pdf' ? '…' : 'PDF'}
                  </button>
                  <button
                    onClick={() => void handleDownload('cv-docx')}
                    disabled={!result.cv || downloading === 'cv-docx'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg text-primary-700 dark:text-primary-400 transition-colors disabled:opacity-60"
                  >
                    <FileText size={13} />{downloading === 'cv-docx' ? '…' : 'Word'}
                  </button>
                </div>
              </div>
              {/* Cover Letter row */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800/50 rounded-xl">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                    <FileText size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Cover Letter</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">Targeted letter for this job</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => void handleDownload('cl-pdf')}
                    disabled={downloading === 'cl-pdf'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg text-red-700 dark:text-red-400 transition-colors disabled:opacity-60"
                  >
                    <FileDown size={13} />{downloading === 'cl-pdf' ? '…' : 'PDF'}
                  </button>
                  <button
                    onClick={() => void handleDownload('cl-docx')}
                    disabled={downloading === 'cl-docx'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg text-primary-700 dark:text-primary-400 transition-colors disabled:opacity-60"
                  >
                    <FileText size={13} />{downloading === 'cl-docx' ? '…' : 'Word'}
                  </button>
                </div>
              </div>
              {/* Combined application PDF */}
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-primary-50 to-indigo-50 dark:from-primary-900/20 dark:to-indigo-900/20 rounded-xl border border-primary-100 dark:border-primary-800/50">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center shrink-0">
                    <Layers size={14} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Combined Application</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">CV + cover letter, one PDF</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => void handleDownload('combined')}
                    disabled={!result.cv || downloading === 'combined'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-primary-300 dark:border-primary-700 bg-primary-600 hover:bg-primary-700 rounded-lg text-white transition-colors disabled:opacity-60"
                  >
                    <FileDown size={13} />{downloading === 'combined' ? '…' : 'PDF'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Resume preview */}
          <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-indigo-600" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Resume</h3>
                <span className="text-xs text-gray-400 dark:text-slate-500">— ATS-optimized for {result.job_title}</span>
              </div>
              <CopyButton text={resumeText} />
            </div>
            <div className="p-5">
              {/* Contact header */}
              <div className="border-b-2 border-primary-600 pb-3 mb-4">
                <h2 className="text-xl font-bold text-slate-800 dark:text-white">{result.resume?.contact?.name}</h2>
                <p className="text-primary-600 font-semibold text-sm mt-0.5">{result.resume?.headline}</p>
                <p className="text-gray-500 dark:text-slate-400 text-xs mt-1">
                  {[result.resume?.contact?.email, result.resume?.contact?.phone, result.resume?.contact?.location].filter(Boolean).join('  ·  ')}
                </p>
              </div>

              {/* Summary */}
              {result.resume?.professional_summary && (
                <div className="mb-4">
                  <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-1.5 border-b border-slate-200 dark:border-slate-700 pb-1">Professional Summary</h4>
                  <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed">{result.resume.professional_summary}</p>
                </div>
              )}

              {/* Core competencies */}
              {(result.resume?.core_competencies?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Core Competencies</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {result.resume.core_competencies.map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs rounded-md border border-slate-200 dark:border-slate-600">{skill}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {(result.resume?.experience?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Professional Experience</h4>
                  {result.resume.experience.map((exp, i) => (
                    <div key={i} className="mb-3">
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm font-bold text-slate-800 dark:text-white">{exp.role}</span>
                        <span className="text-xs text-gray-400 dark:text-slate-500">{exp.dates}</span>
                      </div>
                      <p className="text-xs font-semibold text-primary-600 mb-1">{exp.company}</p>
                      <ul className="space-y-0.5">
                        {(exp.bullets ?? []).map((b, j) => (
                          <li key={j} className="text-sm text-gray-700 dark:text-slate-300 flex items-start gap-2">
                            <span className="text-primary-400 mt-0.5 shrink-0">•</span>
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              {/* Education */}
              {(result.resume?.education?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Education</h4>
                  {result.resume.education.map((edu, i) => (
                    <div key={i} className="flex items-baseline justify-between mb-1">
                      <div>
                        <span className="text-sm font-semibold text-slate-800 dark:text-white">{edu.degree}</span>
                        <span className="text-xs text-gray-500 dark:text-slate-400 ml-2">{edu.school}</span>
                      </div>
                      <span className="text-xs text-gray-400 dark:text-slate-500">{edu.year}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Technical Skills */}
              {result.resume?.technical_skills && Object.keys(result.resume.technical_skills).length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Technical Skills</h4>
                  {Object.entries(result.resume.technical_skills).map(([cat, skills], i) => (
                    <p key={i} className="text-sm text-gray-700 dark:text-slate-300 mb-1">
                      <span className="font-semibold text-slate-800 dark:text-white">{cat}: </span>
                      {(skills as string[]).join(', ')}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CV Preview */}
          {result.cv && (
            <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-primary-600" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Curriculum Vitae</h3>
                  <span className="text-xs text-gray-400 dark:text-slate-500">— Comprehensive career record</span>
                </div>
                <CopyButton text={cvText} />
              </div>
              <div className="p-5">
                {/* CV Header */}
                <div className="bg-slate-800 dark:bg-slate-900 rounded-xl px-5 py-4 mb-5">
                  <h2 className="text-xl font-bold text-white">{result.cv.contact.name}</h2>
                  <p className="text-slate-400 text-xs mt-1">
                    {[result.cv.contact.email, result.cv.contact.phone, result.cv.contact.location].filter(Boolean).join('  ·  ')}
                  </p>
                </div>
                {/* Profile Summary */}
                {result.cv.profile_summary && (
                  <div className="mb-5 p-4 bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500 rounded-r-xl">
                    <h4 className="text-xs font-bold text-primary-700 dark:text-primary-400 uppercase tracking-widest mb-1.5">Professional Profile</h4>
                    <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed">{result.cv.profile_summary}</p>
                  </div>
                )}
                {/* Key Achievements */}
                {(result.cv.key_achievements?.length ?? 0) > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Key Achievements</h4>
                    <ul className="space-y-1.5">
                      {result.cv.key_achievements.slice(0, 6).map((ach, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-slate-300">
                          <span className="text-primary-500 shrink-0 mt-0.5">▪</span>
                          <span>{ach}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Career History */}
                {(result.cv.experience?.length ?? 0) > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Career History</h4>
                    {result.cv.experience.map((exp, i) => (
                      <div key={i} className="mb-4">
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm font-bold text-slate-800 dark:text-white">{exp.role}</span>
                          <span className="text-xs text-gray-400 dark:text-slate-500">{exp.dates}</span>
                        </div>
                        <p className="text-xs font-semibold text-primary-600 mb-1">{exp.company}</p>
                        {exp.description && (
                          <p className="text-xs text-gray-500 dark:text-slate-400 italic mb-1.5">{exp.description}</p>
                        )}
                        <ul className="space-y-0.5">
                          {(exp.bullets ?? []).map((b, j) => (
                            <li key={j} className="text-sm text-gray-700 dark:text-slate-300 flex items-start gap-2">
                              <span className="text-primary-400 shrink-0 mt-0.5">•</span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
                {/* Skills */}
                {result.cv.skills && Object.keys(result.cv.skills).length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Skills & Expertise</h4>
                    <div className="space-y-1">
                      {Object.entries(result.cv.skills).map(([cat, skills], i) => (
                        <p key={i} className="text-xs text-gray-700 dark:text-slate-300">
                          <span className="font-semibold text-slate-700 dark:text-white">{cat}: </span>
                          {(skills as string[]).join(', ')}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                {/* Education */}
                {(result.cv.education?.length ?? 0) > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Education</h4>
                    {result.cv.education.map((edu, i) => (
                      <div key={i} className="flex items-baseline justify-between mb-2">
                        <div>
                          <span className="text-sm font-semibold text-slate-800 dark:text-white">{edu.degree}{edu.field ? `, ${edu.field}` : ''}</span>
                          <span className="text-xs text-gray-500 dark:text-slate-400 ml-2">{edu.school}</span>
                          {edu.honors && <span className="text-xs text-primary-500 ml-1">— {edu.honors}</span>}
                        </div>
                        <span className="text-xs text-gray-400 dark:text-slate-500 ml-4 shrink-0">{edu.year}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Certifications */}
                {(result.cv.certifications?.length ?? 0) > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest mb-2 border-b border-slate-200 dark:border-slate-700 pb-1">Certifications</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {result.cv.certifications.map((cert, i) => (
                        <span key={i} className="px-2 py-0.5 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-xs rounded-md border border-primary-200 dark:border-primary-800">{cert}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cover letter preview */}
          <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-purple-600" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Cover Letter</h3>
              </div>
              <CopyButton text={coverText} />
            </div>
            <div className="p-5 max-w-2xl">
              {/* Cover letter content */}
              <div className="border-b-2 border-primary-600 pb-3 mb-6">
                <h2 className="text-lg font-bold text-slate-800 dark:text-white">{result.resume?.contact?.name}</h2>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                  {[result.resume?.contact?.email, result.resume?.contact?.phone].filter(Boolean).join('   |   ')}
                </p>
              </div>

              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">{result.cover_letter?.date}</p>
              <p className="text-sm text-gray-700 dark:text-slate-300 mb-1">{result.cover_letter?.recipient}</p>
              <p className="text-sm text-gray-700 dark:text-slate-300 mb-6">{result.cover_letter?.company}</p>

              <p className="text-sm font-semibold text-slate-800 dark:text-white mb-5">RE: {result.cover_letter?.subject}</p>

              <p className="text-sm text-gray-700 dark:text-slate-300 mb-4">Dear {result.cover_letter?.recipient},</p>

              <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed mb-4">{result.cover_letter?.opening}</p>
              <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed mb-4">{result.cover_letter?.value_prop}</p>
              <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed mb-6">{result.cover_letter?.closing}</p>

              <p className="text-sm text-gray-700 dark:text-slate-300 mb-6">{result.cover_letter?.sign_off},</p>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">{result.resume?.contact?.name}</p>
            </div>
          </div>

          {/* View original job */}
          <div className="flex justify-center">
            <a
              href={result.job_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
            >
              <ExternalLink size={14} />
              View original job posting
            </a>
          </div>
        </motion.div>
      )}

      {/* History when showing result */}
      {step === 'result' && history.length > 1 && (
        <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Other Applications</h3>
          <div className="space-y-1.5">
            {history.filter((h) => h.id !== result?.id).slice(0, 5).map((item) => (
              <button
                key={item.id}
                onClick={() => void handleLoadHistory(item.id)}
                className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 text-left transition-colors group"
              >
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white truncate">{item.job_title} @ {item.company_name}</p>
                  <p className="text-xs text-gray-400 dark:text-slate-500">{formatAge(item.created_at)}</p>
                </div>
                <span className={`ml-3 px-2 py-0.5 text-xs font-semibold rounded-full border shrink-0 ${atsColor(item.ats_score_estimate)}`}>
                  {item.ats_score_estimate}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobTargeting;
